import { useCallback, useEffect, useRef, useState } from 'react';
import { FiEyeOff } from 'react-icons/fi';
import { Box, Center, Text } from '@chakra-ui/react';

function useScreenCaptureDetection(isActive, onCaptureStart, onCaptureEnd) {
  const activeStreamsRef = useRef(new Set());

  useEffect(() => {
    if (!isActive || !navigator.mediaDevices?.getDisplayMedia) return;

    const original = navigator.mediaDevices.getDisplayMedia.bind(navigator.mediaDevices);

    navigator.mediaDevices.getDisplayMedia = async function (...args) {
      const stream = await original(...args);

      activeStreamsRef.current.add(stream.id);
      onCaptureStart();

      stream.getVideoTracks().forEach((track) => {
        track.addEventListener('ended', () => {
          activeStreamsRef.current.delete(stream.id);

          if (activeStreamsRef.current.size === 0) {
            onCaptureEnd();
          }
        });
      });

      return stream;
    };

    return () => {
      navigator.mediaDevices.getDisplayMedia = original;
    };
  }, [isActive, onCaptureStart, onCaptureEnd]);
}

function useScreenProtection(isActive) {
  const [isObscured, setIsObscured] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const contentRef = useRef(null);

  const applyInstantBlur = useCallback((blur) => {
    const el = contentRef.current;

    if (el) {
      el.style.filter = blur ? 'blur(30px)' : '';
    }
  }, []);

  const handleCaptureStart = useCallback(() => {
    applyInstantBlur(true);
    setIsScreenSharing(true);
  }, [applyInstantBlur]);

  const handleCaptureEnd = useCallback(() => {
    applyInstantBlur(false);
    setIsScreenSharing(false);
  }, [applyInstantBlur]);

  useScreenCaptureDetection(isActive, handleCaptureStart, handleCaptureEnd);

  useEffect(() => {
    if (!isActive) {
      setIsObscured(false);
      setIsScreenSharing(false);

      return;
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        applyInstantBlur(true);
        setIsObscured(true);
      }
    };

    const handleWindowBlur = () => {
      applyInstantBlur(true);
      setIsObscured(true);
    };

    const handleWindowFocus = () => {
      if (!isScreenSharing) {
        applyInstantBlur(false);
      }

      setIsObscured(false);
    };

    const handleKeyDown = (e) => {
      if (e.key === 'PrintScreen') {
        e.preventDefault();
        applyInstantBlur(true);
        setIsObscured(true);
      }

      if ((e.metaKey && e.shiftKey) || (e.ctrlKey && e.shiftKey)) {
        applyInstantBlur(true);
        setIsObscured(true);
      }

      if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S' || e.key === 'p' || e.key === 'P')) {
        e.preventDefault();
      }
    };

    const handleContextMenu = (e) => {
      e.preventDefault();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowFocus);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('contextmenu', handleContextMenu);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [isActive, isScreenSharing, applyInstantBlur]);

  const dismiss = useCallback(() => {
    if (isScreenSharing) return;

    applyInstantBlur(false);
    setIsObscured(false);
  }, [applyInstantBlur, isScreenSharing]);

  return { isObscured: isObscured || isScreenSharing, dismiss, contentRef, isScreenSharing };
}

function ProtectionOverlay({ onDismiss, isScreenSharing }) {
  return (
    <Center
      position='absolute'
      inset={0}
      zIndex={10}
      bg='gray.900'
      flexDirection='column'
      cursor={isScreenSharing ? 'not-allowed' : 'pointer'}
      onClick={isScreenSharing ? undefined : onDismiss}
    >
      <FiEyeOff size={48} color='white' />
      <Text color='white' mt={4} fontSize='lg' fontWeight='semibold'>
        {isScreenSharing ? 'Screen sharing detected' : 'Content hidden for security'}
      </Text>
      <Text color='gray.400' mt={2} fontSize='sm'>
        {isScreenSharing ? 'Documents are hidden while screen sharing is active' : 'Click here to view again'}
      </Text>
    </Center>
  );
}

function WatermarkLayer({ text, viewerIdentity }) {
  return (
    <Box position='absolute' inset={0} zIndex={2} pointerEvents='none' overflow='hidden'>
      {text && (
        <Text
          position='absolute'
          top='50%'
          left='50%'
          transform='translate(-50%, -50%) rotate(-30deg)'
          fontSize='4rem'
          fontWeight='bold'
          color='rgba(0, 0, 0, 0.06)'
          whiteSpace='nowrap'
          letterSpacing='0.2em'
        >
          {text}
        </Text>
      )}
      {viewerIdentity && (
        <>
          <Text
            position='absolute'
            top='15%'
            left='20%'
            transform='rotate(-25deg)'
            fontSize='sm'
            fontWeight='medium'
            color='rgba(0, 0, 0, 0.07)'
            whiteSpace='nowrap'
          >
            {viewerIdentity}
          </Text>
          <Text
            position='absolute'
            bottom='20%'
            right='10%'
            transform='rotate(-25deg)'
            fontSize='sm'
            fontWeight='medium'
            color='rgba(0, 0, 0, 0.07)'
            whiteSpace='nowrap'
          >
            {viewerIdentity}
          </Text>
          <Text
            position='absolute'
            top='60%'
            left='5%'
            transform='rotate(-25deg)'
            fontSize='sm'
            fontWeight='medium'
            color='rgba(0, 0, 0, 0.07)'
            whiteSpace='nowrap'
          >
            {viewerIdentity}
          </Text>
          <Text
            position='absolute'
            top='30%'
            right='5%'
            transform='rotate(-25deg)'
            fontSize='sm'
            fontWeight='medium'
            color='rgba(0, 0, 0, 0.07)'
            whiteSpace='nowrap'
          >
            {viewerIdentity}
          </Text>
        </>
      )}
    </Box>
  );
}

export default function ScreenProtectedView({
  isActive = true,
  children,
  watermarkText = 'CONFIDENTIAL',
  viewerIdentity,
  ...boxProps
}) {
  const { isObscured, dismiss, contentRef, isScreenSharing } = useScreenProtection(isActive);

  return (
    <Box
      position='relative'
      sx={{
        userSelect: 'none',
        WebkitTouchCallout: 'none',
        '@media print': { visibility: 'hidden' },
      }}
      {...boxProps}
    >
      <Box ref={contentRef}>{children}</Box>

      <WatermarkLayer text={watermarkText} viewerIdentity={viewerIdentity} />

      {isObscured && <ProtectionOverlay onDismiss={dismiss} isScreenSharing={isScreenSharing} />}
    </Box>
  );
}
